import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  Database,
  FolderOpen,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import { FolderSelectModal } from "../FolderSelectModal/FolderSelectModal";
import { useContentFolderTree } from "../FolderSelectModal/useContentFolderTree";
import {
  apiFolderPath,
  clipUrl,
  instanceRootFrom,
  stripInstance,
} from "../VizQuerySetupModal/instancePaths";

interface AISetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = "source" | "template" | "target" | "run";
type FolderField = "appFiles" | "jsonFiles" | "svgFiles";

interface SetupOptions {
  app: { id: string; name: string; subdomain: string; agentId: string | null };
  templates: Array<{ id: string; name: string; description: string; category: string }>;
  datasources: Array<{ id: string; name: string }>;
  agents: Array<{ id: string; name: string }>;
  previous: {
    jsonApplicationPath: string;
    jsonPath: string;
    svgPath: string;
    datasourceId: string;
  };
}

interface ProposedTable {
  name: string;
  rows: number;
  columns: Array<{ name: string; type: string }>;
}

interface Proposal {
  /** 'ai' when the model designed the schema, 'structure' when it could not. */
  source?: "ai" | "structure";
  tables?: ProposedTable[];
  script?: string;
  notes?: string[];
  mapping?: unknown;
}

interface Analysis {
  fileCount: number;
  proposal: Proposal;
}

/** One turn of the conversation about the tables. */
interface RefineTurn {
  instruction: string;
  changes?: string[];
  error?: string;
  pending?: boolean;
}

interface InstallChoice {
  schema: boolean;
  playbook: boolean;
  agent: boolean;
}

interface JobStep {
  key: string;
  label: string;
  status: "pending" | "running" | "done" | "failed" | "skipped";
  detail?: string;
}

interface Job {
  id: string;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED";
  steps: JobStep[];
  progress: number;
  error: string | null;
  result: Record<string, any> | null;
}

const STEP_ORDER: Step[] = ["source", "template", "target", "run"];
const STEP_LABEL: Record<Step, string> = {
  source: "Data",
  template: "Template",
  target: "Names",
  run: "Build",
};

const POLL_MS = 2000;
/**
 * A run outlives this panel — it is a server-side job, and closing the toolbox
 * neither stops it nor loses it. Remembering its id is what lets the panel pick
 * the report back up instead of looking like nothing ever happened.
 */
const JOB_KEY = "aiSetup_jobId";

/**
 * Turns the folders this frontend already publishes into something its AI
 * Assistant can answer questions about — a database, a playbook of verified
 * questions and an agent — without anyone opening the analytics console.
 *
 * The work runs on the server as a job (importing and generating queries takes
 * minutes, not seconds), so this panel starts it and then reports what each
 * stage is doing.
 */
export const AISetupModal: React.FC<AISetupModalProps> = ({ isOpen, onClose }) => {
  const { config } = useConfig();
  const { user } = React.useContext(CadviewerContext);

  const [step, setStep] = useState<Step>("source");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [options, setOptions] = useState<SetupOptions | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);

  const [appFiles, setAppFiles] = useState("");
  const [jsonFiles, setJsonFiles] = useState("");
  const [svgFiles, setSvgFiles] = useState("");

  const [templateId, setTemplateId] = useState("");
  const [useExistingDb, setUseExistingDb] = useState(false);
  const [datasourceId, setDatasourceId] = useState("");
  const [databaseName, setDatabaseName] = useState("");
  const [agentName, setAgentName] = useState("");
  const [playbookName, setPlaybookName] = useState("");
  const [importScript, setImportScript] = useState("");
  const [showScript, setShowScript] = useState(false);
  const [install, setInstall] = useState<InstallChoice>({
    schema: true,
    playbook: true,
    agent: true,
  });
  const [turns, setTurns] = useState<RefineTurn[]>([]);
  const [instruction, setInstruction] = useState("");
  const [refining, setRefining] = useState(false);

  const [job, setJob] = useState<Job | null>(null);
  const pollRef = useRef<number | null>(null);

  const [pickerField, setPickerField] = useState<FolderField | null>(null);
  const instanceRoot = useMemo(() => instanceRootFrom(config), [config]);
  const { folderStructure } = useContentFolderTree(isOpen);

  const token = user?.token;
  const apiBase = useMemo(
    () =>
      (config.analyticsApiUrl || config.ServerBackEndUrl || "http://localhost:3000").replace(
        /\/+$/,
        ""
      ),
    [config.analyticsApiUrl, config.ServerBackEndUrl]
  );

  const call = useCallback(
    async (path: string, init?: RequestInit) => {
      const headers = new Headers(init?.headers);
      headers.set("Accept", "application/json");
      if (init?.body) headers.set("Content-Type", "application/json");
      if (token) headers.set("Authorization", `Bearer ${token}`);

      const response = await fetch(`${apiBase}/api/v1/tenant/vizquery${path}`, {
        ...init,
        headers,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message =
          payload.message || payload.error || `Request failed (${response.status})`;
        // The two answers a power user is most likely to meet deserve to say
        // what to do about them, not what the status code was. A role refusal
        // arrives as a 401 like an expired token does, so the text decides.
        if (/not authorized/i.test(message) || response.status === 403) {
          throw new Error(
            "Your account is not an administrator of this site, so it cannot run the AI Setup."
          );
        }
        if (response.status === 401) {
          throw new Error("Your session has expired — sign in again and reopen this panel.");
        }
        throw new Error(message);
      }
      return payload;
    },
    [apiBase, token]
  );

  // Open on the folders this frontend already reads, so the common case is a
  // single click on "Analyze".
  useEffect(() => {
    if (!isOpen) return;
    setAppFiles(clipUrl(config.globalApplicationFilesLocation, instanceRoot));
    setJsonFiles(clipUrl(config.propertyJSONFilesLocation, instanceRoot));
    setSvgFiles(clipUrl(config.propertySVGFilesLocation, instanceRoot));
  }, [isOpen, config, instanceRoot]);

  // Pick a run back up when the panel is reopened while it is still going.
  useEffect(() => {
    if (!isOpen || !token) return;
    const saved = window.localStorage.getItem(JOB_KEY);
    if (!saved) return;
    let cancelled = false;
    call(`/jobs/${saved}`)
      .then((data: Job) => {
        if (cancelled) return;
        if (data.status === "COMPLETED" || data.status === "FAILED") {
          window.localStorage.removeItem(JOB_KEY);
          return;
        }
        setJob(data);
        setStep("run");
      })
      .catch(() => window.localStorage.removeItem(JOB_KEY));
    return () => {
      cancelled = true;
    };
  }, [isOpen, token, call]);

  useEffect(() => {
    if (!isOpen || !token) return;
    let cancelled = false;
    call("/options")
      .then((data: SetupOptions) => {
        if (cancelled) return;
        setOptions(data);
        const label = data.app.name || data.app.subdomain;
        setDatabaseName((current) => current || `${label} data`);
        setAgentName((current) => current || `${label} assistant`);
        setPlaybookName((current) => current || `${label} playbook`);

        // A frontend whose content URLs were never configured has nothing to
        // pre-fill from — but a previous run recorded exactly these folders.
        const previous = data.previous ?? ({} as SetupOptions["previous"]);
        const fillFrom = (value: string, fallback: string) =>
          value && value !== "/" ? value : fallback ? `/${fallback}` : value;
        setAppFiles((current) => fillFrom(current, previous.jsonApplicationPath));
        setJsonFiles((current) => fillFrom(current, previous.jsonPath));
        setSvgFiles((current) => fillFrom(current, previous.svgPath));

        // Second run: import into the database the first one created rather
        // than scattering the same site's data over several.
        if (previous.datasourceId && data.datasources.some((d) => d.id === previous.datasourceId)) {
          setUseExistingDb(true);
          setDatasourceId(previous.datasourceId);
        }
      })
      .catch((err: Error) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [isOpen, token, call]);

  // Follow the run. The interval is cleared on unmount and as soon as the job
  // reaches a final state, so closing the panel mid-run leaves nothing behind.
  useEffect(() => {
    if (!job || job.status === "COMPLETED" || job.status === "FAILED") return;
    pollRef.current = window.setInterval(async () => {
      try {
        const fresh: Job = await call(`/jobs/${job.id}`);
        setJob(fresh);
        if (fresh.status === "COMPLETED" || fresh.status === "FAILED") {
          window.localStorage.removeItem(JOB_KEY);
        }
      } catch {
        /* a missed poll is not a failure; the next one will tell */
      }
    }, POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [job, call]);

  const folderPayload = () => ({
    jsonApplicationPath: apiFolderPath(appFiles),
    jsonPath: apiFolderPath(jsonFiles),
    svgPath: apiFolderPath(svgFiles),
  });

  const runAnalysis = async () => {
    setError(null);
    if (!appFiles || !jsonFiles || !svgFiles) {
      setError("Pick the application JSON, JSON content and SVG folders first.");
      return;
    }
    setBusy(true);
    try {
      const data = await call("/analyze", {
        method: "POST",
        body: JSON.stringify(folderPayload()),
      });
      setAnalysis(data);
      setImportScript(data.proposal?.script || "");
      setStep("template");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const refineTables = async () => {
    const asked = instruction.trim();
    if (!asked || !analysis?.proposal?.mapping) return;
    setError(null);
    setInstruction("");
    setTurns((t) => [...t, { instruction: asked, pending: true }]);
    setRefining(true);
    try {
      const data = await call("/refine", {
        method: "POST",
        body: JSON.stringify({
          ...folderPayload(),
          mapping: analysis.proposal.mapping,
          instruction: asked,
        }),
      });
      setAnalysis({ fileCount: data.fileCount, proposal: data.proposal });
      setImportScript(data.proposal?.script || "");
      setTurns((t) =>
        t.map((turn, i) => (i === t.length - 1 ? { ...turn, pending: false, changes: data.changes } : turn))
      );
    } catch (err) {
      const message = (err as Error).message;
      setTurns((t) =>
        t.map((turn, i) => (i === t.length - 1 ? { ...turn, pending: false, error: message } : turn))
      );
    } finally {
      setRefining(false);
    }
  };

  const startSetup = async () => {
    setError(null);
    if (!useExistingDb && !databaseName.trim()) {
      setError("Name the database to create.");
      return;
    }
    if (useExistingDb && !datasourceId) {
      setError("Pick the database to import into.");
      return;
    }
    if (!importScript.trim()) {
      setError("The import script is empty — run the analysis again.");
      return;
    }
    setBusy(true);
    try {
      const started = await call("/setup", {
        method: "POST",
        body: JSON.stringify({
          ...folderPayload(),
          databaseName: useExistingDb ? undefined : databaseName.trim(),
          datasourceId: useExistingDb ? datasourceId : undefined,
          importScript: importScript.trim(),
          agentName: agentName.trim() || undefined,
          playbookName: playbookName.trim() || undefined,
          marketplaceTemplateId: templateId || undefined,
          install,
        }),
      });
      window.localStorage.setItem(JOB_KEY, started.id);
      setJob(started);
      setStep("run");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleFolderSelect = (path: string) => {
    let formatted = stripInstance(path.startsWith("/") ? path : `/${path}`, instanceRoot);
    if (!formatted.startsWith("/")) formatted = `/${formatted}`;
    const finalPath = formatted.endsWith("/") ? formatted : `${formatted}/`;

    if (pickerField === "appFiles") setAppFiles(finalPath);
    if (pickerField === "jsonFiles") setJsonFiles(finalPath);
    if (pickerField === "svgFiles") setSvgFiles(finalPath);
  };

  if (!isOpen) return null;

  // ─── Sign-in gate ──────────────────────────────────────────────────────
  if (!token) {
    return (
      <div className="px-4 py-6 bg-white">
        <div className="flex gap-3 items-start rounded-md bg-amber-50 border border-amber-200 p-3">
          <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <p className="font-semibold">Sign in to run the AI Setup</p>
            <p className="mt-1 text-amber-800">
              This builds a database and an assistant for this site, so it needs an administrator
              account of this frontend.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentIndex = STEP_ORDER.indexOf(step);

  return (
    <>
      <div className="flex flex-col bg-white">
        {/* Stepper */}
        <div className="flex items-center gap-1 px-3 py-2.5 border-b border-slate-200 bg-slate-50/70">
          {STEP_ORDER.map((name, index) => {
            const done = index < currentIndex;
            const active = index === currentIndex;
            return (
              <React.Fragment key={name}>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                      done
                        ? "bg-emerald-500 text-white"
                        : active
                          ? "bg-indigo-600 text-white"
                          : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {done ? <Check className="h-3 w-3" /> : index + 1}
                  </span>
                  <span
                    className={`text-[11px] ${
                      active ? "font-semibold text-indigo-700" : "text-slate-500"
                    }`}
                  >
                    {STEP_LABEL[name]}
                  </span>
                </div>
                {index < STEP_ORDER.length - 1 && (
                  <span className="flex-1 h-px bg-slate-200 mx-0.5" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="px-3 py-3 max-h-[58vh] overflow-y-auto">
          {step === "source" && (
            <SourceStep
              folders={[
                { key: "appFiles", label: "Application JSON", value: appFiles, set: setAppFiles },
                { key: "jsonFiles", label: "JSON content", value: jsonFiles, set: setJsonFiles },
                { key: "svgFiles", label: "SVG floor plans", value: svgFiles, set: setSvgFiles },
              ]}
              onPick={setPickerField}
            />
          )}

          {step === "template" && (
            <TemplateStep
              analysis={analysis}
              templates={options?.templates ?? []}
              selected={templateId}
              onSelect={setTemplateId}
            />
          )}

          {step === "target" && (
            <TargetStep
              install={install}
              setInstall={setInstall}
              turns={turns}
              instruction={instruction}
              setInstruction={setInstruction}
              onRefine={refineTables}
              refining={refining}
              canRefine={!!analysis?.proposal?.mapping}
              boundAgentName={
                options?.app.agentId
                  ? (options.agents.find((a) => a.id === options.app.agentId)?.name ??
                    "another assistant")
                  : null
              }
              analysis={analysis}
              datasources={options?.datasources ?? []}
              useExistingDb={useExistingDb}
              setUseExistingDb={setUseExistingDb}
              datasourceId={datasourceId}
              setDatasourceId={setDatasourceId}
              databaseName={databaseName}
              setDatabaseName={setDatabaseName}
              agentName={agentName}
              setAgentName={setAgentName}
              playbookName={playbookName}
              setPlaybookName={setPlaybookName}
              importScript={importScript}
              setImportScript={setImportScript}
              showScript={showScript}
              setShowScript={setShowScript}
            />
          )}

          {step === "run" && <RunStep job={job} appName={options?.app.name ?? ""} />}

          {error && (
            <div className="mt-3 flex gap-2 items-start rounded-md bg-rose-50 border border-rose-200 p-2.5">
              <AlertCircle className="h-3.5 w-3.5 text-rose-600 mt-0.5 shrink-0" />
              <p className="text-[11px] text-rose-800 leading-relaxed">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-slate-200 bg-slate-50">
          <button
            type="button"
            onClick={() => {
              if (step === "source" || step === "run") {
                onClose();
                return;
              }
              setError(null);
              setStep(STEP_ORDER[currentIndex - 1]);
            }}
            disabled={busy && step !== "run"}
            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {step === "source" || step === "run" ? (
              "Close"
            ) : (
              <>
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </>
            )}
          </button>

          {step === "source" && (
            <PrimaryButton onClick={runAnalysis} busy={busy} icon={<Sparkles className="h-3.5 w-3.5" />}>
              {busy ? "Reading the folders…" : "Analyze the data"}
            </PrimaryButton>
          )}
          {step === "template" && (
            <PrimaryButton onClick={() => setStep("target")} busy={false} icon={<ChevronRight className="h-3.5 w-3.5" />}>
              Continue
            </PrimaryButton>
          )}
          {step === "target" && (
            <PrimaryButton onClick={startSetup} busy={busy} icon={<Sparkles className="h-3.5 w-3.5" />}>
              {busy ? "Starting…" : "Build the assistant"}
            </PrimaryButton>
          )}
          {step === "run" && (job?.status === "RUNNING" || job?.status === "PENDING") && (
            <span className="text-[10.5px] text-slate-500">
              Closing is safe — the run continues.
            </span>
          )}
          {step === "run" && job?.status === "FAILED" && (
            <PrimaryButton onClick={() => setStep("target")} busy={false} icon={<ArrowLeft className="h-3.5 w-3.5" />}>
              Change the settings
            </PrimaryButton>
          )}
        </div>
      </div>

      <FolderSelectModal
        isOpen={pickerField !== null}
        onClose={() => setPickerField(null)}
        onSelect={handleFolderSelect}
        folderStructure={folderStructure}
        title={
          pickerField === "appFiles"
            ? "Select Folder - Application JSON"
            : pickerField === "jsonFiles"
              ? "Select Folder - JSON Content"
              : "Select Folder - SVG Floor Plans"
        }
      />
    </>
  );
};

// ─── Steps ────────────────────────────────────────────────────────────────

const SourceStep: React.FC<{
  folders: Array<{ key: FolderField; label: string; value: string; set: (v: string) => void }>;
  onPick: (field: FolderField) => void;
}> = ({ folders, onPick }) => (
  <div className="space-y-3">
    <p className="text-[11px] leading-relaxed text-slate-600">
      The assistant learns from the JSON these folders publish. The SVG plans are recorded but never
      read — the space association happens here in the viewer.
    </p>
    {folders.map((folder) => (
      <div key={folder.key}>
        <label className="block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
          {folder.label}
        </label>
        <div className="mt-1 flex">
          <input
            type="text"
            value={folder.value}
            onChange={(e) => folder.set(e.target.value)}
            className="min-w-0 flex-1 rounded-l-md border border-slate-300 px-2 py-1.5 font-mono text-[11px] text-slate-700 outline-none focus:border-indigo-500"
          />
          <button
            type="button"
            onClick={() => onPick(folder.key)}
            title="Browse"
            className="inline-flex items-center gap-1 rounded-r-md border border-l-0 border-slate-300 bg-white px-2.5 text-slate-600 hover:bg-slate-50"
          >
            <FolderOpen className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    ))}
  </div>
);

const TemplateStep: React.FC<{
  analysis: Analysis | null;
  templates: SetupOptions["templates"];
  selected: string;
  onSelect: (id: string) => void;
}> = ({ analysis, templates, selected, onSelect }) => (
  <div className="space-y-3">
    {analysis && <ProposalSummary analysis={analysis} />}
    <p className="text-[11px] leading-relaxed text-slate-600">
      A marketplace template gives the assistant its personality and its tools. The tables always
      come from your own data.
    </p>

    <div className="space-y-1.5">
      {templates.map((template) => (
        <button
          key={template.id}
          type="button"
          onClick={() => onSelect(template.id)}
          className={`w-full rounded-md border px-2.5 py-2 text-left transition ${
            selected === template.id
              ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
              : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center gap-1.5">
            <Bot className="h-3.5 w-3.5 shrink-0 text-indigo-500" />
            <span className="text-xs font-semibold text-slate-800">{template.name}</span>
          </div>
          {template.description && (
            <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-500">
              {template.description}
            </p>
          )}
        </button>
      ))}

      <button
        type="button"
        onClick={() => onSelect("")}
        className={`w-full rounded-md border px-2.5 py-2 text-left transition ${
          selected === ""
            ? "border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500"
            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
        }`}
      >
        <span className="text-xs font-semibold text-slate-800">No template</span>
        <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
          A plain assistant that answers from the imported data.
        </p>
      </button>
    </div>
  </div>
);

/**
 * What the analysis found, and — when the model could not design a schema —
 * that it was the structure of the files that did. A demo must never present a
 * degraded proposal as if it were the intended one.
 */
const ProposalSummary: React.FC<{ analysis: Analysis }> = ({ analysis }) => {
  const tables = analysis.proposal?.tables ?? [];
  const rows = tables.reduce((total, table) => total + (table.rows ?? 0), 0);
  const fallback = analysis.proposal?.source === 'structure';
  const notes = analysis.proposal?.notes ?? [];

  return (
    <div
      className={`rounded-md border px-2.5 py-2 text-[11px] ${
        fallback ? "border-amber-200 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900"
      }`}
    >
      <div>
        <span className="font-semibold">{analysis.fileCount} JSON file(s)</span> read ·{" "}
        <span className="font-semibold">{tables.length} table(s)</span> ·{" "}
        {rows.toLocaleString()} row(s) found
      </div>
      {fallback && (
        <div className="mt-1 font-semibold">
          The AI could not design the schema — these tables follow the raw structure of the files.
        </div>
      )}
      {notes.length > 0 && (
        <ul className="mt-1 space-y-0.5 opacity-80">
          {notes.slice(0, 4).map((note) => (
            <li key={note}>· {note}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const TargetStep: React.FC<{
  install: InstallChoice;
  setInstall: (v: InstallChoice) => void;
  turns: RefineTurn[];
  instruction: string;
  setInstruction: (v: string) => void;
  onRefine: () => void;
  refining: boolean;
  canRefine: boolean;
  boundAgentName: string | null;
  analysis: Analysis | null;
  datasources: SetupOptions["datasources"];
  useExistingDb: boolean;
  setUseExistingDb: (v: boolean) => void;
  datasourceId: string;
  setDatasourceId: (v: string) => void;
  databaseName: string;
  setDatabaseName: (v: string) => void;
  agentName: string;
  setAgentName: (v: string) => void;
  playbookName: string;
  setPlaybookName: (v: string) => void;
  importScript: string;
  setImportScript: (v: string) => void;
  showScript: boolean;
  setShowScript: (v: boolean) => void;
}> = (props) => (
  <div className="space-y-3">
    <div>
      <span className="block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
        What to build
      </span>
      <div className="mt-1 space-y-1">
        {(
          [
            ['schema', 'Schema', 'The tables, described so the AI can read them.'],
            ['playbook', 'Playbook', 'Verified questions written and tested against your data.'],
            ['agent', 'Assistant', 'The agent this site’s AI Assistant talks to.'],
          ] as Array<[keyof InstallChoice, string, string]>
        ).map(([key, label, hint]) => (
          <label key={key} className="flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              checked={props.install[key]}
              onChange={(e) => {
                const next = { ...props.install, [key]: e.target.checked };
                // A playbook hangs from a schema, and an assistant answers over
                // one: ticking either brings it along rather than failing later.
                if ((key === 'playbook' || key === 'agent') && e.target.checked) next.schema = true;
                if (key === 'schema' && !e.target.checked) {
                  next.playbook = false;
                  next.agent = false;
                }
                props.setInstall(next);
              }}
              className="mt-0.5 h-3 w-3 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="leading-tight">
              <span className="text-[11px] font-medium text-slate-700">{label}</span>
              <span className="block text-[10.5px] text-slate-500">{hint}</span>
            </span>
          </label>
        ))}
      </div>
      <p className="mt-1 text-[10.5px] text-slate-500">
        The data is imported either way — that is what this does.
      </p>
    </div>

    {props.boundAgentName && (
      <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-relaxed text-amber-900">
        This site currently answers with <strong>{props.boundAgentName}</strong>. Finishing here
        points its AI Assistant at the new one instead.
      </div>
    )}
    <div>
      <label className="block text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
        Database
      </label>
      <select
        value={props.useExistingDb ? props.datasourceId : "__new__"}
        onChange={(e) => {
          const isNew = e.target.value === "__new__";
          props.setUseExistingDb(!isNew);
          props.setDatasourceId(isNew ? "" : e.target.value);
        }}
        className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-indigo-500"
      >
        <option value="__new__">Create a new database</option>
        {props.datasources.map((source) => (
          <option key={source.id} value={source.id}>
            {source.name}
          </option>
        ))}
      </select>
      {!props.useExistingDb && (
        <input
          type="text"
          value={props.databaseName}
          onChange={(e) => props.setDatabaseName(e.target.value)}
          placeholder="UrbanEdge data"
          className="mt-1.5 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-indigo-500"
        />
      )}
    </div>

    <LabelledInput
      label="Assistant name"
      icon={<Bot className="h-3 w-3 text-slate-400" />}
      value={props.agentName}
      onChange={props.setAgentName}
      placeholder="UrbanEdge assistant"
    />
    <LabelledInput
      label="Playbook name"
      icon={<Database className="h-3 w-3 text-slate-400" />}
      value={props.playbookName}
      onChange={props.setPlaybookName}
      placeholder="UrbanEdge playbook"
    />

    <div className="rounded-md border border-slate-200">
      <button
        type="button"
        onClick={() => props.setShowScript(!props.showScript)}
        className="flex w-full items-center justify-between px-2.5 py-2 text-left"
      >
        <span className="text-[11px] font-semibold text-slate-600">
          Import script
          {props.analysis?.proposal?.tables?.length
            ? ` · ${props.analysis.proposal.tables.length} table(s)`
            : ""}
        </span>
        {props.showScript ? (
          <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
        )}
      </button>
      {props.showScript && (
        <div className="border-t border-slate-200 p-2">
          {!!props.analysis?.proposal?.tables?.length && (
            <table className="mb-2 w-full text-[10.5px]">
              <tbody>
                {props.analysis.proposal.tables.map((table) => (
                  <tr key={table.name} className="border-b border-slate-100 last:border-0">
                    <td className="py-1 pr-2 font-mono text-slate-700">{table.name}</td>
                    <td className="py-1 pr-2 text-right tabular-nums text-slate-500">
                      {table.rows.toLocaleString()} row(s)
                    </td>
                    <td className="py-1 text-right tabular-nums text-slate-500">
                      {table.columns.length} col.
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <textarea
            value={props.importScript}
            onChange={(e) => props.setImportScript(e.target.value)}
            spellCheck={false}
            className="h-40 w-full resize-y rounded border border-slate-200 bg-slate-50 p-2 font-mono text-[10.5px] leading-relaxed text-slate-700 outline-none focus:border-indigo-500"
          />
          <p className="mt-1 text-[10.5px] text-slate-500">
            Written by the AI from a sample of your files. Edit it if a field belongs somewhere else.
          </p>

          {props.canRefine && (
            <div className="mt-2 border-t border-slate-200 pt-2">
              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
                Ask for a change
              </span>
              {props.turns.length > 0 && (
                <ul className="mt-1 space-y-1.5">
                  {props.turns.map((turn, index) => (
                    <li key={index} className="text-[10.5px] leading-snug">
                      <span className="text-slate-700">“{turn.instruction}”</span>
                      {turn.pending && <span className="ml-1 text-slate-400">· rewriting…</span>}
                      {turn.error && <span className="block text-rose-700">{turn.error}</span>}
                      {turn.changes?.map((change) => (
                        <span key={change} className="block text-emerald-700">
                          · {change}
                        </span>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-1 flex gap-1">
                <input
                  type="text"
                  value={props.instruction}
                  onChange={(e) => props.setInstruction(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !props.refining) {
                      e.preventDefault();
                      props.onRefine();
                    }
                  }}
                  placeholder="Merge the two detail tables, call it tenants…"
                  className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-[11px] text-slate-700 outline-none focus:border-indigo-500"
                />
                <button
                  type="button"
                  onClick={props.onRefine}
                  disabled={props.refining || !props.instruction.trim()}
                  className="inline-flex items-center rounded-md bg-slate-700 px-2.5 text-[11px] font-semibold text-white hover:bg-slate-600 disabled:opacity-40"
                >
                  {props.refining ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

const RunStep: React.FC<{ job: Job | null; appName: string }> = ({ job, appName }) => {
  if (!job) {
    return (
      <div className="flex items-center gap-2 py-6 text-xs text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Starting the run…
      </div>
    );
  }

  const done = job.status === "COMPLETED";
  const result = job.result ?? {};

  return (
    <div className="space-y-3">
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            job.status === "FAILED" ? "bg-rose-500" : done ? "bg-emerald-500" : "bg-indigo-500"
          }`}
          style={{ width: `${Math.max(job.progress, 4)}%` }}
        />
      </div>

      <ul className="space-y-1.5">
        {job.steps.map((step) => (
          <li key={step.key} className="flex items-start gap-2">
            <StepIcon status={step.status} />
            <div className="min-w-0">
              <p
                className={`text-[11px] leading-tight ${
                  step.status === "pending" ? "text-slate-400" : "font-medium text-slate-700"
                }`}
              >
                {step.label}
              </p>
              {step.detail && (
                <p className="text-[10.5px] leading-snug text-slate-500">{step.detail}</p>
              )}
            </div>
          </li>
        ))}
      </ul>

      {done && result.warning && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
          <p className="text-[11px] leading-relaxed text-amber-900">{result.warning}</p>
        </div>
      )}

      {done && !result.warning && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5">
          <p className="text-[11px] font-semibold text-emerald-900">
            {result.agent ? `${result.agent.name} is ready` : "Your data is imported"}
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-emerald-800">
            {result.import?.rowsInserted?.toLocaleString() ?? 0} row(s) in{" "}
            {result.import?.tables?.length ?? 0} table(s) of{" "}
            {result.datasource?.name ?? "your data"}
            {result.playbook ? `, ${result.playbook.verified} verified question(s)` : ""}
            {result.agent ? `. Ask it something from the AI Assistant of ${appName || "this site"}.` : "."}
          </p>

          {/* The page in front of the reader is holding the chat session it
              opened with: the new assistant answers, but only after a reload. */}
          {result.refreshPage && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-500"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Reload this page to use it
            </button>
          )}
          {result.wiring?.redeployStarted && (
            <p className="mt-2 text-[10.5px] text-emerald-800">
              This site is rebuilding to switch its AI Assistant on — it takes a few minutes, then
              reload the page.
            </p>
          )}
        </div>
      )}

      {job.status === "FAILED" && job.error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5">
          <p className="text-[11px] leading-relaxed text-rose-800">{job.error}</p>
        </div>
      )}
    </div>
  );
};

// ─── Small pieces ─────────────────────────────────────────────────────────

const StepIcon: React.FC<{ status: JobStep["status"] }> = ({ status }) => {
  if (status === "done") {
    return (
      <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-emerald-500">
        <Check className="h-2.5 w-2.5 text-white" />
      </span>
    );
  }
  if (status === "running") {
    return <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-indigo-500" />;
  }
  if (status === "failed") {
    return <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />;
  }
  return <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-slate-300 ml-[3px] mr-[3px]" />;
};

const PrimaryButton: React.FC<{
  onClick: () => void;
  busy: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ onClick, busy, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
  >
    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : icon}
    {children}
  </button>
);

const LabelledInput: React.FC<{
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}> = ({ label, icon, value, onChange, placeholder }) => (
  <div>
    <label className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
      {icon}
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-indigo-500"
    />
  </div>
);
